import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type QueryFunctionContext,
  type QueryKey,
} from '@tanstack/react-query';
import {
  fetchCongregationMe,
  fetchCongregationMasters,
  fetchCongregationProfile,
  fetchCongregationApplications,
  fetchCongregationApplicationDetail,
  fetchMentorApplications,
  fetchCongregationContacts,
  fetchCongregationAddresses,
  fetchCongregationFamily,
  fetchManualRelatives,
  fetchCongregationChanting,
  fetchCongregationBooks,
  fetchCongregationCourses,
  fetchCongregationMilestones,
  fetchCongregationDocuments,
  fetchCongregationPhotos,
  isApiError,
  type FetchCongregationProfileParams,
  fetchCongregationBirthdayReport,
  fetchCongregationAnniversaryReport,
  type CongregationReportQueryParams,
  fetchCongregationHouseholds,
  fetchCongregationHouseholdDetail,
  type FetchCongregationHouseholdsParams,
  type FetchMentorApplicationsParams,
  fetchMentorNotifications,
  fetchMentorAuditEvents,
  fetchSadhanaTemplates,
  createSadhanaTemplate,
  updateSadhanaTemplate,
  deleteSadhanaTemplate,
  fetchSadhanaTemplateAssignment,
  fetchSadhanaTemplateAssignments,
  assignSadhanaTemplateDefaults,
  fetchSadhanaWeeks,
  fetchSadhanaWeekDetail,
  updateSadhanaMentorComment,
  fetchSadhanaPrescriptions,
  createSadhanaPrescription,
  fetchSadhanaChantingAnalytics,
  mentorAcceptApplication,
  mentorRejectApplication,
  type FetchSadhanaTemplatesParams,
  type FetchSadhanaTemplateAssignmentParams,
  type FetchSadhanaTemplateAssignmentsParams,
  type FetchSadhanaWeeksParams,
  type FetchSadhanaWeekDetailParams,
  type FetchSadhanaPrescriptionsParams,
  type FetchSadhanaChantingAnalyticsParams,
  type MentorReviewApplicationRequest,
  type MentorReviewApplicationResponse,
} from '../api';
import { congregationKeys } from '../query/keys';
import { flags } from '../config/flags';
import type {
  CongregationApplication,
  CongregationApplicationsResponse,
  CongregationMeResponse,
  CongregationMastersPayload,
  CongregationProfileSnapshot,
  CongregationMentorApplicationsResponse,
  CongregationContactPoint,
  CongregationAddress,
  CongregationFamilyRelation,
  CongregationManualRelative,
  CongregationChantingRecord,
  CongregationBookProgress,
  CongregationCourseProgress,
  CongregationMilestone,
  CongregationDocument,
  CongregationPhoto,
  CongregationBirthdayReportEntry,
  CongregationAnniversaryReportEntry,
  CongregationHousehold,
  CongregationMentorNotificationsResponse,
  CongregationMentorAuditEventsResponse,
  SadhanaTemplateListResponse,
  SadhanaTemplateCreateRequest,
  SadhanaTemplateCreateResponse,
  SadhanaTemplateUpdateRequest,
  SadhanaTemplateUpdateResponse,
  SadhanaTemplateDeleteResponse,
  SadhanaTemplateDefaultAssignmentRequest,
  SadhanaTemplateAssignmentResponse,
  SadhanaTemplateAssignmentsResponse,
  SadhanaTemplateAssignDefaultsResponse,
  SadhanaWeeksResponse,
  SadhanaWeekDetailResponse,
  ChantingPrescriptionsResponse,
  ChantingPrescriptionResponse,
  ChantingPrescriptionCreateRequest,
  SadhanaChantingAnalyticsResponse,
  SadhanaCardWeek,
  SadhanaMentorCommentRequest,
} from '../types/congregation';
import type { ApiError } from '../api';

type BaseOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, QueryKey>,
  'queryKey' | 'queryFn'
>;

export const useCongregationMeQuery = (
  options?: BaseOptions<CongregationMeResponse>,
): UseQueryResult<CongregationMeResponse, ApiError> =>
  useQuery<CongregationMeResponse, ApiError>({
    queryKey: congregationKeys.me(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationMe(signal),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    throwOnError: false,
    ...options,
  });

export const useCongregationMastersQuery = (
  options?: BaseOptions<CongregationMastersPayload>,
): UseQueryResult<CongregationMastersPayload, ApiError> =>
  useQuery<CongregationMastersPayload, ApiError>({
    queryKey: congregationKeys.masters(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationMasters(signal),
    staleTime: 10 * 60_000,
    gcTime: 20 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationProfileQuery = (
  params?: FetchCongregationProfileParams,
  options?: BaseOptions<CongregationProfileSnapshot>,
): UseQueryResult<CongregationProfileSnapshot, ApiError> =>
  useQuery<CongregationProfileSnapshot, ApiError>({
    queryKey: congregationKeys.profile(params?.memberId),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationProfile(params ?? {}, signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: 'always',
    ...options,
  });

export const useCongregationContactsQuery = (
  options?: BaseOptions<CongregationContactPoint[]>,
): UseQueryResult<CongregationContactPoint[], ApiError> =>
  useQuery<CongregationContactPoint[], ApiError>({
    queryKey: congregationKeys.contacts(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationContacts(signal),
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationAddressesQuery = (
  options?: BaseOptions<CongregationAddress[]>,
): UseQueryResult<CongregationAddress[], ApiError> =>
  useQuery<CongregationAddress[], ApiError>({
    queryKey: congregationKeys.addresses(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationAddresses(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationFamilyQuery = (
  options?: BaseOptions<CongregationFamilyRelation[]>,
): UseQueryResult<CongregationFamilyRelation[], ApiError> =>
  useQuery<CongregationFamilyRelation[], ApiError>({
    queryKey: congregationKeys.family(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationFamily(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useManualRelativesQuery = (
  options?: BaseOptions<CongregationManualRelative[]>,
): UseQueryResult<CongregationManualRelative[], ApiError> =>
  useQuery<CongregationManualRelative[], ApiError>({
    queryKey: congregationKeys.manualRelatives(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchManualRelatives(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationChantingQuery = (
  options?: BaseOptions<CongregationChantingRecord[]>,
): UseQueryResult<CongregationChantingRecord[], ApiError> =>
  useQuery<CongregationChantingRecord[], ApiError>({
    queryKey: congregationKeys.chanting(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationChanting(signal),
    staleTime: 20_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationBooksQuery = (
  options?: BaseOptions<CongregationBookProgress[]>,
): UseQueryResult<CongregationBookProgress[], ApiError> =>
  useQuery<CongregationBookProgress[], ApiError>({
    queryKey: congregationKeys.books(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationBooks(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationCoursesQuery = (
  options?: BaseOptions<CongregationCourseProgress[]>,
): UseQueryResult<CongregationCourseProgress[], ApiError> =>
  useQuery<CongregationCourseProgress[], ApiError>({
    queryKey: congregationKeys.courses(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationCourses(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationMilestonesQuery = (
  options?: BaseOptions<CongregationMilestone[]>,
): UseQueryResult<CongregationMilestone[], ApiError> =>
  useQuery<CongregationMilestone[], ApiError>({
    queryKey: congregationKeys.milestones(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationMilestones(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationDocumentsQuery = (
  options?: BaseOptions<CongregationDocument[]>,
): UseQueryResult<CongregationDocument[], ApiError> =>
  useQuery<CongregationDocument[], ApiError>({
    queryKey: congregationKeys.documents(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationDocuments(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationPhotosQuery = (
  options?: BaseOptions<CongregationPhoto[]>,
): UseQueryResult<CongregationPhoto[], ApiError> =>
  useQuery<CongregationPhoto[], ApiError>({
    queryKey: congregationKeys.photos(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationPhotos(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationHouseholdsQuery = (
  params?: FetchCongregationHouseholdsParams,
  options?: BaseOptions<CongregationHousehold[]>,
): UseQueryResult<CongregationHousehold[], ApiError> =>
  useQuery<CongregationHousehold[], ApiError>({
    queryKey: congregationKeys.households(params),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationHouseholds(params, signal),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: 'always',
    ...options,
  });

export const useCongregationHouseholdDetailQuery = (
  householdId?: number,
  options?: BaseOptions<CongregationHousehold>,
): UseQueryResult<CongregationHousehold, ApiError> =>
  useQuery<CongregationHousehold, ApiError>({
    queryKey: congregationKeys.householdDetail(householdId ?? -1),
    queryFn: ({ signal }: QueryFunctionContext) => {
      if (typeof householdId !== 'number') {
        throw new Error('householdId is required');
      }
      return fetchCongregationHouseholdDetail(householdId, signal);
    },
    enabled: typeof householdId === 'number',
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: 'always',
    ...options,
  });

export const useCongregationApplicationsQuery = (
  options?: BaseOptions<CongregationApplicationsResponse>,
): UseQueryResult<CongregationApplicationsResponse, ApiError> =>
  useQuery<CongregationApplicationsResponse, ApiError>({
    queryKey: congregationKeys.applications(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationApplications(signal),
    staleTime: 15_000,
    gcTime: 2 * 60_000,
    retry: 1,
    refetchOnWindowFocus: 'always',
    ...options,
  });

export const useCongregationApplicationDetailQuery = (
  applicationId?: number,
  options?: BaseOptions<CongregationApplication>,
): UseQueryResult<CongregationApplication, ApiError> =>
  useQuery<CongregationApplication, ApiError>({
    queryKey: congregationKeys.applicationDetail(applicationId ?? -1),
    queryFn: ({ signal }: QueryFunctionContext) => {
      if (typeof applicationId !== 'number') {
        throw new Error('applicationId is required');
      }
      return fetchCongregationApplicationDetail(applicationId, signal);
    },
    enabled: typeof applicationId === 'number',
    staleTime: 15_000,
    gcTime: 2 * 60_000,
    retry: 1,
    ...options,
  });

export const useMentorApplicationsQuery = (
  params?: FetchMentorApplicationsParams,
  options?: BaseOptions<CongregationMentorApplicationsResponse>,
): UseQueryResult<CongregationMentorApplicationsResponse, ApiError> =>
  useQuery<CongregationMentorApplicationsResponse, ApiError>({
    queryKey: congregationKeys.mentorApplications({
      statuses: params?.statuses,
      search: params?.search,
    }),
    queryFn: ({ signal }: QueryFunctionContext) => fetchMentorApplications(params, signal),
    staleTime: 20_000,
    gcTime: 2 * 60_000,
    retry: 1,
    refetchOnWindowFocus: 'always',
    ...options,
  });

export const useCongregationBirthdaysQuery = (
  params?: CongregationReportQueryParams,
  options?: BaseOptions<CongregationBirthdayReportEntry[]>,
): UseQueryResult<CongregationBirthdayReportEntry[], ApiError> =>
  useQuery<CongregationBirthdayReportEntry[], ApiError>({
    queryKey: congregationKeys.birthdays(params),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationBirthdayReport(params, signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useCongregationAnniversariesQuery = (
  params?: CongregationReportQueryParams,
  options?: BaseOptions<CongregationAnniversaryReportEntry[]>,
): UseQueryResult<CongregationAnniversaryReportEntry[], ApiError> =>
  useQuery<CongregationAnniversaryReportEntry[], ApiError>({
    queryKey: congregationKeys.anniversaries(params),
    queryFn: ({ signal }: QueryFunctionContext) => fetchCongregationAnniversaryReport(params, signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useMentorNotificationsQuery = (
  options?: BaseOptions<CongregationMentorNotificationsResponse>,
): UseQueryResult<CongregationMentorNotificationsResponse, ApiError> =>
  useQuery<CongregationMentorNotificationsResponse, ApiError>({
    queryKey: congregationKeys.mentorNotifications(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchMentorNotifications(signal),
    staleTime: 30_000,
    gcTime: 2 * 60_000,
    retry: 1,
    refetchInterval: 45_000,
    refetchOnWindowFocus: 'always',
    ...options,
    enabled: flags.congregationMentorNotifications && (options?.enabled ?? true),
  });

export const useMentorAuditEventsQuery = (
  options?: BaseOptions<CongregationMentorAuditEventsResponse>,
): UseQueryResult<CongregationMentorAuditEventsResponse, ApiError> =>
  useQuery<CongregationMentorAuditEventsResponse, ApiError>({
    queryKey: congregationKeys.mentorAuditEvents(),
    queryFn: ({ signal }: QueryFunctionContext) => fetchMentorAuditEvents(signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
    ...options,
    enabled: flags.congregationMentorAuditLog && (options?.enabled ?? true),
  });

export const useSadhanaTemplatesQuery = (
  params?: FetchSadhanaTemplatesParams,
  options?: BaseOptions<SadhanaTemplateListResponse>,
): UseQueryResult<SadhanaTemplateListResponse, ApiError> =>
  useQuery<SadhanaTemplateListResponse, ApiError>({
    queryKey: congregationKeys.mentorSadhanaTemplates(params),
    queryFn: ({ signal }: QueryFunctionContext) => fetchSadhanaTemplates(params, signal),
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: 1,
    ...options,
  });

export const useSadhanaTemplateAssignmentQuery = (
  params?: FetchSadhanaTemplateAssignmentParams,
  options?: BaseOptions<SadhanaTemplateAssignmentResponse>,
): UseQueryResult<SadhanaTemplateAssignmentResponse, ApiError> =>
  useQuery<SadhanaTemplateAssignmentResponse, ApiError>({
    queryKey: congregationKeys.mentorSadhanaTemplateAssignment(params?.memberId ?? 'self'),
    queryFn: ({ signal }: QueryFunctionContext) => fetchSadhanaTemplateAssignment(params, signal),
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    ...options,
  });

export const useSadhanaTemplateAssignmentsQuery = (
  params?: FetchSadhanaTemplateAssignmentsParams,
  options?: BaseOptions<SadhanaTemplateAssignmentsResponse>,
): UseQueryResult<SadhanaTemplateAssignmentsResponse, ApiError> =>
  useQuery<SadhanaTemplateAssignmentsResponse, ApiError>({
    queryKey: congregationKeys.mentorSadhanaTemplateAssignments(params?.memberId ?? 'self'),
    queryFn: ({ signal }: QueryFunctionContext) => fetchSadhanaTemplateAssignments(params, signal),
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    ...options,
  });

export const useSadhanaWeeksQuery = (
  params?: FetchSadhanaWeeksParams,
  options?: BaseOptions<SadhanaWeeksResponse>,
): UseQueryResult<SadhanaWeeksResponse, ApiError> =>
  useQuery<SadhanaWeeksResponse, ApiError>({
    queryKey: congregationKeys.mentorSadhanaWeeks({
      memberId: params?.memberId ?? null,
      limit: params?.limit,
      before: params?.before ?? null,
    }),
    queryFn: ({ signal }: QueryFunctionContext) => fetchSadhanaWeeks(params, signal),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useSadhanaWeekDetailQuery = (
  weekId: number | null | undefined,
  params?: FetchSadhanaWeekDetailParams,
  options?: BaseOptions<SadhanaWeekDetailResponse | null>,
): UseQueryResult<SadhanaWeekDetailResponse | null, ApiError> =>
  useQuery<SadhanaWeekDetailResponse | null, ApiError>({
    queryKey: congregationKeys.mentorSadhanaWeekDetail(weekId ?? 0, params?.memberId ?? 'self'),
    queryFn: async ({ signal }: QueryFunctionContext) => {
      if (!weekId) {
        throw new Error('Week id missing');
      }
      try {
        return await fetchSadhanaWeekDetail(weekId, params, signal);
      } catch (error) {
        if (isApiError(error) && (error.status === 403 || error.status === 404)) {
          return null;
        }
        throw error;
      }
    },
    enabled: typeof weekId === 'number' && weekId > 0 && (options?.enabled ?? true),
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    retry: 1,
    ...options,
  });

export const useSadhanaPrescriptionsQuery = (
  params?: FetchSadhanaPrescriptionsParams,
  options?: BaseOptions<ChantingPrescriptionsResponse>,
): UseQueryResult<ChantingPrescriptionsResponse, ApiError> =>
  useQuery<ChantingPrescriptionsResponse, ApiError>({
    queryKey: congregationKeys.mentorSadhanaPrescriptions(params?.memberId ?? 'self'),
    queryFn: ({ signal }: QueryFunctionContext) => fetchSadhanaPrescriptions(params, signal),
    staleTime: 45_000,
    gcTime: 10 * 60_000,
    retry: 1,
    ...options,
  });

export const useSadhanaChantingAnalyticsQuery = (
  params?: FetchSadhanaChantingAnalyticsParams,
  options?: BaseOptions<SadhanaChantingAnalyticsResponse>,
): UseQueryResult<SadhanaChantingAnalyticsResponse, ApiError> =>
  useQuery<SadhanaChantingAnalyticsResponse, ApiError>({
    queryKey: congregationKeys.mentorSadhanaAnalytics({
      weeks: params?.weeks ?? null,
      since: params?.since ?? null,
      until: params?.until ?? null,
      devoteeMemberId: params?.devoteeMemberId ?? null,
    }),
    queryFn: ({ signal }: QueryFunctionContext) => fetchSadhanaChantingAnalytics(params, signal),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    ...options,
  });

type SadhanaCommentVariables = {
  weekId: number;
  payload: SadhanaMentorCommentRequest;
};

export const useSadhanaMentorCommentMutation = (
  options?: UseMutationOptions<{ week: SadhanaCardWeek }, ApiError, SadhanaCommentVariables>,
): UseMutationResult<{ week: SadhanaCardWeek }, ApiError, SadhanaCommentVariables> =>
  useMutation<{ week: SadhanaCardWeek }, ApiError, SadhanaCommentVariables>({
    mutationFn: ({ weekId, payload }) => updateSadhanaMentorComment(weekId, payload),
    ...options,
  });

type SadhanaPrescriptionVariables = {
  memberId?: number;
  payload: ChantingPrescriptionCreateRequest;
};

export const useSadhanaCreatePrescriptionMutation = (
  options?: UseMutationOptions<ChantingPrescriptionResponse, ApiError, SadhanaPrescriptionVariables>,
): UseMutationResult<ChantingPrescriptionResponse, ApiError, SadhanaPrescriptionVariables> =>
  useMutation<ChantingPrescriptionResponse, ApiError, SadhanaPrescriptionVariables>({
    mutationFn: ({ memberId, payload }) => createSadhanaPrescription({ memberId }, payload),
    ...options,
  });

type SadhanaTemplateCreateVariables = {
  payload: SadhanaTemplateCreateRequest;
};

export const useCreateSadhanaTemplateMutation = (
  options?: UseMutationOptions<SadhanaTemplateCreateResponse, ApiError, SadhanaTemplateCreateVariables>,
): UseMutationResult<SadhanaTemplateCreateResponse, ApiError, SadhanaTemplateCreateVariables> =>
  useMutation<SadhanaTemplateCreateResponse, ApiError, SadhanaTemplateCreateVariables>({
    mutationFn: ({ payload }) => createSadhanaTemplate(payload),
    ...options,
  });

type SadhanaTemplateUpdateVariables = {
  templateId: number;
  payload: SadhanaTemplateUpdateRequest;
};

export const useUpdateSadhanaTemplateMutation = (
  options?: UseMutationOptions<SadhanaTemplateUpdateResponse, ApiError, SadhanaTemplateUpdateVariables>,
): UseMutationResult<SadhanaTemplateUpdateResponse, ApiError, SadhanaTemplateUpdateVariables> =>
  useMutation<SadhanaTemplateUpdateResponse, ApiError, SadhanaTemplateUpdateVariables>({
    mutationFn: ({ templateId, payload }) => updateSadhanaTemplate(templateId, payload),
    ...options,
  });

type SadhanaTemplateDeleteVariables = {
  templateId: number;
};

export const useDeleteSadhanaTemplateMutation = (
  options?: UseMutationOptions<SadhanaTemplateDeleteResponse, ApiError, SadhanaTemplateDeleteVariables>,
): UseMutationResult<SadhanaTemplateDeleteResponse, ApiError, SadhanaTemplateDeleteVariables> =>
  useMutation<SadhanaTemplateDeleteResponse, ApiError, SadhanaTemplateDeleteVariables>({
    mutationFn: ({ templateId }) => deleteSadhanaTemplate(templateId),
    ...options,
  });

type SadhanaTemplateAssignDefaultsVariables = {
  templateId: number;
  payload: SadhanaTemplateDefaultAssignmentRequest;
};

export const useAssignSadhanaTemplateDefaultsMutation = (
  options?: UseMutationOptions<
    SadhanaTemplateAssignDefaultsResponse,
    ApiError,
    SadhanaTemplateAssignDefaultsVariables
  >,
): UseMutationResult<
  SadhanaTemplateAssignDefaultsResponse,
  ApiError,
  SadhanaTemplateAssignDefaultsVariables
> =>
  useMutation<SadhanaTemplateAssignDefaultsResponse, ApiError, SadhanaTemplateAssignDefaultsVariables>({
    mutationFn: ({ templateId, payload }) => assignSadhanaTemplateDefaults(templateId, payload),
    ...options,
  });

// Mentor Application Review Mutations

interface MentorReviewApplicationVariables {
  applicationId: number;
  payload: MentorReviewApplicationRequest;
}

export const useMentorAcceptApplicationMutation = (
  options?: UseMutationOptions<
    MentorReviewApplicationResponse,
    ApiError,
    MentorReviewApplicationVariables
  >,
): UseMutationResult<
  MentorReviewApplicationResponse,
  ApiError,
  MentorReviewApplicationVariables
> =>
  useMutation<MentorReviewApplicationResponse, ApiError, MentorReviewApplicationVariables>({
    mutationFn: ({ applicationId, payload }) => mentorAcceptApplication(applicationId, payload),
    ...options,
  });

export const useMentorRejectApplicationMutation = (
  options?: UseMutationOptions<
    MentorReviewApplicationResponse,
    ApiError,
    MentorReviewApplicationVariables
  >,
): UseMutationResult<
  MentorReviewApplicationResponse,
  ApiError,
  MentorReviewApplicationVariables
> =>
  useMutation<MentorReviewApplicationResponse, ApiError, MentorReviewApplicationVariables>({
    mutationFn: ({ applicationId, payload }) => mentorRejectApplication(applicationId, payload),
    ...options,
  });

